import { ProjectWithAssigneesDto } from '../../../commonTypes/acs';
import { AssigneeDto, ProjectAssigneesWithUserInfoDto, EmploymentType } from '../../../commonTypes/project.types';
import { useSelector, useDispatch } from 'react-redux';
import { selectProjectAssignees, setProjectAssignees } from '../../../store/slices/project-assignees.slice';
import { selectAuth } from '../../../store/slices/auth.slice';
import { selectOrganizationMembers } from '../../../store/slices/add-edit-policy.slice';
import {
    invalidateSingleProjectCache,
    invalidateProjectMetrics,
    invalidateTimesheetCache,
    invalidateActivityLogsCache,
} from '../../../store/slices/projects.slice';
import { RootState } from '../../../store';
import { useEffect, useState, useMemo } from 'react';
import AssignMembersDrawer from '../../AssignMembersDrawer';
import { ActivityFeed } from './ActivityFeed';
import { MetricCard } from '../../Common/MetricCard';
import { MemberCard } from '../MemberCard';
import { useHttpGetProjectRoles, useHttpPostProjectAssignees, useHttpGetProjectTotalEarnings, useOrganizationId } from '../../../hooks';
import useHttpPatchProjectAssignee from '../../../hooks/project/projectApi/useHttpPatchProjectAssignee';
import useHttpDeleteProjectAssignee from '../../../hooks/project/projectApi/useHttpDeleteProjectAssignee';
import { useHttpGetProjectHoursSummary, useHttpGetUserTimeLogs } from '../../../hooks/timelog/timelogApi';
import useHttpGetMembersOMX from '../../../hooks/user/userApi/useHttpGetMembersOMX';
import { RateType } from '@cyberlabrs/helpers';
import { DateTime } from 'luxon';
import { getCurrencySymbol } from '../../../util/general';
import { formatDateAndTime } from '../../../util/date/dateFormater';
import { checkIfAdminOrOwner } from '../../../api/utils/roleUtils';

interface ProjectOverviewTabProps {
    project: ProjectWithAssigneesDto;
    onEditMember?: (member: ProjectAssigneesWithUserInfoDto) => void;
    onRefresh?: () => void;
    canEdit?: boolean;
    showRates?: boolean;
    showMetrics?: boolean;
    showOnlyOwnData?: boolean;
    currentUserSub?: string;
    currency?: string;
}

export const ProjectOverviewTab = ({
    project,
    onRefresh,
    canEdit = true,
    showRates = true,
    showMetrics,
    showOnlyOwnData = false,
    currency = 'USD',
}: ProjectOverviewTabProps) => {
    const dispatch = useDispatch();
    const [isAssignMemberDrawerOpen, setIsAssignMemberDrawerOpen] = useState(false);
    const [editingMember, setEditingMember] = useState<ProjectAssigneesWithUserInfoDto | null>(null);

    const organizationId = useOrganizationId();
    const projectAssignees = useSelector((s: RootState) => selectProjectAssignees(s, project.id));
    const authUser = useSelector(selectAuth);
    const organizationMembers = useSelector(selectOrganizationMembers);

    const { exec: fetchOrgMembers } = useHttpGetMembersOMX(organizationId);

    const canViewMetrics = showMetrics ?? checkIfAdminOrOwner(authUser.role.name);
    const { roles, exec: fetchProjectRoles } = useHttpGetProjectRoles(false);
    const { exec: execAddAssignees } = useHttpPostProjectAssignees();
    const { exec: execPatchAssignee } = useHttpPatchProjectAssignee();
    const { execDelete: execDeleteAssignee } = useHttpDeleteProjectAssignee();
    const { data: hoursSummary } = useHttpGetProjectHoursSummary(project.id, canViewMetrics);
    const { data: earningsData } = useHttpGetProjectTotalEarnings(project.id, canViewMetrics);

    const currentMonth = DateTime.now();
    const month = currentMonth.toFormat('MMM');
    const year = currentMonth.year;

    const { currentMonthHours, totalLoggedHours } = useHttpGetUserTimeLogs(
        showOnlyOwnData ? { month, year, projectId: project.id } : { month: '', year: 0, projectId: '' }
    );

    useEffect(() => {
        if (project.projectAssignees?.length) {
            dispatch(setProjectAssignees({ projectId: project.id, assignees: project.projectAssignees }));
        }
    }, [project.projectAssignees, project.id, dispatch]);

    useEffect(() => {
        if (isAssignMemberDrawerOpen && canEdit) {
            if (organizationMembers.length === 0) {
                fetchOrgMembers();
            }
            if (roles.length === 0) {
                fetchProjectRoles();
            }
        }
    }, [isAssignMemberDrawerOpen, canEdit, organizationMembers.length, roles.length, fetchOrgMembers, fetchProjectRoles]);

    const assignees = projectAssignees.length ? projectAssignees : project.projectAssignees || [];

    const activeMembers = useMemo(() => {
        return assignees.filter(a => a.active);
    }, [assignees]);

    const availableMembers = useMemo(() => {
        return organizationMembers.map(m => ({
            label: `${m.givenName} ${m.familyName}`,
            value: m.userSub,
            imageUrl: m.imageUrl,
            jobTitle: m.title,
        }));
    }, [organizationMembers]);

    const assignedUserSubs = useMemo(() => new Set(assignees.map(a => a.userSub)), [assignees]);

    const unassignedMembers = useMemo(() => {
        return availableMembers.filter(m => !assignedUserSubs.has(m.value));
    }, [availableMembers, assignedUserSubs]);

    const employmentTypes = [
        { label: 'Full Time', value: 'Full-time' },
        { label: 'Part Time', value: 'Part-time' },
        { label: 'Contract', value: 'Contract' },
    ];

    const handleEditMember = (member: ProjectAssigneesWithUserInfoDto) => {
        setEditingMember(member);
        setIsAssignMemberDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setIsAssignMemberDrawerOpen(false);
        setEditingMember(null);
    };

    const handleUnassignMember = () => {
        if (editingMember) {
            execDeleteAssignee(editingMember.userSub, project.id, {
                onSuccess: () => {
                    dispatch(invalidateSingleProjectCache(project.id));
                    dispatch(invalidateProjectMetrics(project.id));
                    dispatch(invalidateTimesheetCache(project.id));
                    dispatch(invalidateActivityLogsCache(project.id));
                    handleCloseDrawer();
                    onRefresh?.();
                },
            });
        }
    };

    const handleAssignMembers = (members: any[]) => {
        if (editingMember && members.length === 1) {
            const m = members[0];
            const bill = m.billableType === 'billable';

            execPatchAssignee(
                project.id,
                editingMember.userSub,
                {
                    billable: bill,
                    rate: bill ? parseFloat(m.rate) || 0 : 0,
                    rateType: bill ? (m.rateType === RateType.FIXED ? RateType.FIXED : RateType.HOURLY) : RateType.HOURLY,
                    employmentType: m.employmentType as EmploymentType,
                },
                {
                    onSuccess: () => {
                        dispatch(invalidateSingleProjectCache(project.id));
                        dispatch(invalidateProjectMetrics(project.id));
                        dispatch(invalidateTimesheetCache(project.id));
                        dispatch(invalidateActivityLogsCache(project.id));
                        handleCloseDrawer();
                        onRefresh?.();
                    },
                }
            );
        } else {
            const payload: AssigneeDto[] = members.map(m => {
                const bill = m.billableType === 'billable';
                return {
                    userSub: m.memberId,
                    billable: bill,
                    rate: bill ? parseFloat(m.rate) || 0 : undefined,
                    rateType: bill ? (m.rateType === RateType.FIXED ? RateType.FIXED : RateType.HOURLY) : undefined,
                    employmentType: bill ? m.employmentType : undefined,
                    assignedRoles: [{ projectRoleId: Number(m.role) }],
                };
            });

            execAddAssignees(
                project.id,
                { assignees: payload },
                {
                    onSuccess: () => {
                        dispatch(invalidateSingleProjectCache(project.id));
                        dispatch(invalidateProjectMetrics(project.id));
                        dispatch(invalidateTimesheetCache(project.id));
                        dispatch(invalidateActivityLogsCache(project.id));
                        handleCloseDrawer();
                        onRefresh?.();
                    },
                }
            );
        }
    };

    const formatHours = (hours: number): string => {
        const totalHours = Math.floor(hours);
        return `${totalHours}h `;
    };

    return (
        <div className="project-overview-container">
            <div className="project-overview">
                {canViewMetrics && (
                    <div className="project-overview-tab__metrics">
                        <MetricCard
                            icon="/svg/dollar.svg"
                            title="Total Earnings"
                            value={`${getCurrencySymbol(currency)} ${
                                earningsData?.totalEarnings?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'
                            }`}
                            // change={earningsData?.percentageDifference}
                        />
                        <MetricCard
                            icon="/svg/clock.svg"
                            title="Total Logged Hours"
                            value={`${hoursSummary?.totalSubmittedHours?.toLocaleString('en-US') || '0'} Hours`}
                            // change={
                            //     hoursSummary
                            //         ? hoursSummary.changeDirection === 'down'
                            //             ? -hoursSummary.percentageChange
                            //             : hoursSummary.changeDirection === 'up'
                            //             ? hoursSummary.percentageChange
                            //             : undefined
                            //         : undefined
                            // }
                        />
                        <MetricCard
                            icon="/svg/member.svg"
                            title="Total Members"
                            value={`${activeMembers.length} Member${activeMembers.length !== 1 ? 's' : ''}`}
                        />
                    </div>
                )}

                {showOnlyOwnData && (
                    <div className="project-overview-tab__metrics">
                        <MetricCard icon="/svg/clock.svg" title="This Month" value={formatHours(currentMonthHours)} />
                        <MetricCard icon="/svg/clock.svg" title="Total Logged Hours" value={formatHours(totalLoggedHours)} />
                        <MetricCard
                            icon="/svg/member.svg"
                            title="Team"
                            value={`${activeMembers.length} Member${activeMembers.length !== 1 ? 's' : ''}`}
                        />
                    </div>
                )}

                <div className="project-overview-tab">
                    <div className="project-overview-tab__section">
                        <div className="project-overview-tab__section-title">
                            <h2 className="project-overview-tab__title">{project.name}</h2>
                            {project.endDate && (
                                <p className="project-overview-tab__description">
                                    <span>Project End Date:</span>
                                    <span className="project-overview-tab__end-date">{formatDateAndTime(project.endDate, true)}</span>
                                </p>
                            )}
                        </div>

                        {project.description && <p className="project-overview-tab__description">{project.description}</p>}
                    </div>

                    <div className="project-overview-tab__section">
                        <div className="project-overview-tab__section-header">
                            <h3 className="project-overview-tab__section-title">Members</h3>
                        </div>

                        <div className="project-overview-tab__members">
                            {activeMembers.length ? (
                                <>
                                    {activeMembers.map(m => (
                                        <MemberCard
                                            key={m.userSub}
                                            member={m}
                                            canEdit={canEdit}
                                            showRates={showRates}
                                            onEdit={handleEditMember}
                                            currency={currency}
                                        />
                                    ))}

                                    {canEdit && (
                                        <div className="member-card__add-member" onClick={() => setIsAssignMemberDrawerOpen(true)}>
                                            <div className="member-card__add-member-circle">
                                                <img src="/svg/plus.svg" alt="Add member" title="Add member" />
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="member-card__add-member" onClick={() => setIsAssignMemberDrawerOpen(true)}>
                                    <div className="member-card__add-member-circle">
                                        <img src="/svg/plus.svg" alt="Add member" title="Add member" />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <AssignMembersDrawer
                    open={isAssignMemberDrawerOpen}
                    onClose={handleCloseDrawer}
                    onAssign={handleAssignMembers}
                    onUnassign={handleUnassignMember}
                    availableMembers={unassignedMembers}
                    employmentTypes={employmentTypes}
                    projectRoles={roles}
                    editMode={!!editingMember}
                    editMember={editingMember || undefined}
                    currency={currency}
                />
            </div>

            <ActivityFeed projectId={project.id} clientId={project.clientId} />
        </div>
    );
};
